import { doc, setDoc, updateDoc, serverTimestamp, increment } from 'firebase/firestore';
import { getDb } from '../firebase';
import { getDeviceTimezone } from '../utils/timezoneUtils';

// In-memory dedupe so rapid re-renders during app boot don't re-check storage.
// Resets when the app process restarts — which is fine, storage backs it up.
const sessionMemo = { day: null, uid: null };
const pageviewMemo = new Set();
// Attribution is captured at most once per process boot (storage backs it up
// across page loads). The flag prevents re-reading + re-writing storage on
// every pageview when the user navigates internally with no new UTM params.
const attributionMemo = { lastTouchSource: null };

export const ATTR_FIRST_TOUCH_KEY = 'cps:attr:first';
export const ATTR_LAST_TOUCH_KEY = 'cps:attr:last';

function todayKey() {
  // YYYY-MM-DD in UTC. A user online at midnight UTC is counted for both days —
  // standard industry behavior; simpler than per-user timezone normalization.
  return new Date().toISOString().slice(0, 10);
}

function noopStorage() {
  return { get: async () => null, set: async () => {} };
}

// Sources we deliberately recognize. utm_source values outside this list
// collapse to 'other' so the dashboard stays bounded.
//
// 'invite' / 'appstore' / 'playstore' are mobile-originated buckets. Mobile has
// no document.referrer, so without them every app signup collapsed to 'direct'
// and the viral invite loop was invisible in the dashboard.
const KNOWN_SOURCES = new Set([
  'threads', 'x', 'linkedin', 'instagram', 'facebook', 'tiktok', 'reddit',
  'youtube', 'google', 'bing', 'duckduckgo', 'direct', 'referral', 'other',
  'invite', 'appstore', 'playstore',
]);

// Referrer host substring → source bucket. First match wins.
const REFERRER_HOST_MAP = [
  ['threads.net', 'threads'],
  ['twitter.com', 'x'],
  ['x.com', 'x'],
  ['t.co', 'x'],
  ['linkedin.com', 'linkedin'],
  ['lnkd.in', 'linkedin'],
  ['instagram.com', 'instagram'],
  ['facebook.com', 'facebook'],
  ['fb.com', 'facebook'],
  ['tiktok.com', 'tiktok'],
  ['reddit.com', 'reddit'],
  ['youtube.com', 'youtube'],
  ['youtu.be', 'youtube'],
  ['google.', 'google'],
  ['bing.com', 'bing'],
  ['duckduckgo.com', 'duckduckgo'],
];

function sanitizeSource(raw) {
  if (!raw) return null;
  const s = String(raw).toLowerCase().slice(0, 32).replace(/[^a-z0-9_-]/g, '');
  if (!s) return null;
  // Normalize twitter → x — we use 'x' everywhere in the dashboard.
  if (s === 'twitter') return 'x';
  return KNOWN_SOURCES.has(s) ? s : 'other';
}

function inferSourceFromReferrer(referrer) {
  if (!referrer) return null;
  const lower = referrer.toLowerCase();
  for (const [needle, source] of REFERRER_HOST_MAP) {
    if (lower.includes(needle)) return source;
  }
  return 'referral';
}

function parseUtm(search) {
  if (!search) return {};
  try {
    const qs = String(search).startsWith('?') ? String(search) : `?${search}`;
    const params = new URLSearchParams(qs);
    const out = {};
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
      const v = params.get(key);
      if (v) out[key] = v.slice(0, 128);
    }
    return out;
  } catch {
    return {};
  }
}

async function captureAttribution({ search, referrer, store }) {
  const utm = parseUtm(search);
  const hasSignal = !!utm.utm_source || !!referrer;
  if (!hasSignal) return null;

  const source = sanitizeSource(utm.utm_source) || inferSourceFromReferrer(referrer) || 'direct';
  const payload = {
    utm_source: utm.utm_source || null,
    utm_medium: utm.utm_medium || null,
    utm_campaign: utm.utm_campaign || null,
    utm_content: utm.utm_content || null,
    utm_term: utm.utm_term || null,
    referrer: referrer || null,
    source,
    ts: Date.now(),
  };

  try {
    const existingFirst = await store.get(ATTR_FIRST_TOUCH_KEY);
    if (!existingFirst) await store.set(ATTR_FIRST_TOUCH_KEY, JSON.stringify(payload));
    // Last-touch overwrites only when the source actually changes — avoids
    // pointless storage churn on internal navigation that re-fires the hook.
    if (attributionMemo.lastTouchSource !== source) {
      await store.set(ATTR_LAST_TOUCH_KEY, JSON.stringify(payload));
      attributionMemo.lastTouchSource = source;
    }
  } catch {
    // Non-fatal — storage may be locked in private mode.
  }

  return source;
}

/**
 * Record a session for the current user once per UTC day.
 *
 * Writes two docs on first boot of the day:
 *   1. platformStats/{YYYY-MM-DD}/activeUsers/{uid} — counted by the daily rollup for DAU/MAU
 *   2. users/{uid} — merges lastActiveAt, lastPlatform, platforms.{platform}=true
 *      so we know which platforms each user has ever used.
 *
 * @param {object} opts
 * @param {string} opts.uid       - Firebase Auth UID
 * @param {string} opts.platform  - 'web' | 'ios' | 'android'
 * @param {{get, set}} [opts.storage] - Platform storage adapter (sessionStorage on web,
 *                                      AsyncStorage on mobile). Used to persist the
 *                                      per-day dedupe across reloads.
 */
export async function recordSession({ uid, platform, storage }) {
  if (!uid || !platform) return;

  const day = todayKey();
  if (sessionMemo.day === day && sessionMemo.uid === uid) return;

  const store = storage || noopStorage();
  const storageKey = `cps:session:${uid}:${day}`;

  try {
    const seen = await store.get(storageKey);
    if (seen) {
      sessionMemo.day = day;
      sessionMemo.uid = uid;
      return;
    }
  } catch {
    // If storage read fails, fall through and just write Firestore — the
    // setDoc merge is idempotent anyway.
  }

  const db = getDb();

  try {
    await setDoc(
      doc(db, `platformStats/${day}/activeUsers/${uid}`),
      { platform, firstSeenAt: serverTimestamp() },
      { merge: true }
    );
  } catch (err) {
    // Telemetry must never break the app. Swallow and continue.
    console.warn('[analytics] session ping failed', err?.message || err);
    return;
  }

  // Stamp last-active / platform / timezone on the user's PROFILE doc — but only
  // UPDATE it, never create it. A `setDoc(..., {merge:true})` here would conjure
  // a bare users/{uid} doc, and because the OAuth signup handlers gate their
  // profile write on `!userDoc.exists()`, that stub makes them SKIP writing
  // createdAt/email/signupPlatform. The result is a user with no createdAt —
  // invisible to the new-account rollup query (see functions/metrics/dailyRollup
  // countNewAccounts) and under-reported forever. `updateDoc` no-ops (throws,
  // caught below) when the profile doesn't exist yet, so the signup flow always
  // wins the race and the fields self-heal on the next ping once it does.
  //
  // Using updateDoc also fixes the `platforms.${platform}` key: setDoc treats a
  // dotted key as a LITERAL field name, so it was writing "platforms.ios" as a
  // top-level field instead of the nested platforms map computePlatformSplit reads.
  //
  // Timezone: written only when the platform reports one (a reduced Intl can't
  // clobber a good stored value), so the nightly handoff reminder can fire at 6pm
  // in EACH parent's own zone.
  const timezone = getDeviceTimezone();
  try {
    await updateDoc(doc(db, `users/${uid}`), {
      lastActiveAt: serverTimestamp(),
      lastPlatform: platform,
      [`platforms.${platform}`]: true,
      ...(timezone ? { timezone } : {}),
    });
  } catch (err) {
    // Expected when the profile doc doesn't exist yet (brand-new user whose
    // signup profile write hasn't landed). Non-fatal — activeUsers is already
    // recorded above, and these fields are re-stamped on the next day's ping.
    console.warn('[analytics] session ping profile update skipped', err?.message || err);
  }

  try {
    await store.set(storageKey, '1');
  } catch {
    // Non-fatal.
  }
  sessionMemo.day = day;
  sessionMemo.uid = uid;
}

/**
 * Record a public-page impression, deduped per storage session per path per day.
 *
 * Uses FieldValue.increment on pageviews/{YYYY-MM-DD}. The write is anonymous
 * (rules allow unauth) so it works on the landing page before a user signs in.
 *
 * Attribution capture (UTM + referrer) runs on every call, before the per-path
 * dedupe — last-touch needs to update on internal navigation when a fresh UTM
 * arrives, even if the path was already counted today.
 *
 * @param {object} opts
 * @param {string} opts.path - The route path (e.g. '/', '/blog', '/blog/my-post')
 * @param {string} [opts.search] - URL query string from the current location (web only)
 * @param {string} [opts.referrer] - External document.referrer (caller pre-strips same-origin)
 * @param {{get, set}} [opts.storage] - Session-scoped storage to prevent double-counting
 */
export async function recordPageview({ path, search = '', referrer = '', storage }) {
  if (!path) return;
  const store = storage || noopStorage();

  // Attribution runs on every pageview — captures first-touch + last-touch.
  // Returns the bucketed source for this hit so we can roll it into the doc below.
  const liveSource = await captureAttribution({ search, referrer, store });

  const day = todayKey();
  const memoKey = `${day}:${path}`;
  if (pageviewMemo.has(memoKey)) return;

  const storageKey = `cps:pv:${day}:${path}`;

  try {
    if (await store.get(storageKey)) {
      pageviewMemo.add(memoKey);
      return;
    }
  } catch {
    // Fall through.
  }

  // Classify the path into a bucket so the dashboard can show landing vs blog totals.
  const isBlog = path === '/blog' || path.startsWith('/blog/');
  const isLanding = path === '/';
  const bucketUpdate = {};
  if (isLanding) bucketUpdate.landing = increment(1);
  if (isBlog) bucketUpdate.blog = increment(1);

  // Source bucket for this pageview: prefer live signal, fall back to 'direct'.
  const sourceBucket = liveSource || 'direct';

  const db = getDb();
  try {
    await setDoc(
      doc(db, `pageviews/${day}`),
      {
        total: increment(1),
        ...bucketUpdate,
        [`byPath.${sanitizePathKey(path)}`]: increment(1),
        [`bySource.${sourceBucket}`]: increment(1),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    console.warn('[analytics] pageview ping failed', err?.message || err);
    return;
  }

  try {
    await store.set(storageKey, '1');
  } catch {
    // Non-fatal.
  }
  pageviewMemo.add(memoKey);
}

// ─── Event tracking ──────────────────────────────────────────────────────────

/**
 * Events we deliberately record. Anything not on this list is dropped with a
 * warning rather than written.
 *
 * The allowlist exists because every event becomes a Firestore map key on a
 * single daily document. An open-ended `track()` lets one typo ('expense_added'
 * vs 'expense_created') silently fork a metric, and lets a render loop grow the
 * doc past the 1MiB limit — at which point ALL event tracking for that day
 * fails, not just the offender. Adding an event here is intentionally a code
 * change someone reviews.
 *
 * Naming: `object_verb`, past tense, snake_case.
 */
export const TRACKED_EVENTS = new Set([
  // Onboarding funnel — the biggest measurement gap. Outcomes were tracked
  // (funnelJoinedFamily) but not the steps, so wizard drop-off was invisible.
  'onboarding_started',
  'onboarding_step_completed',
  'onboarding_completed',
  'onboarding_invite_skipped',
  'onboarding_invite_sent',

  // Tutorial — state was persisted per-user but never aggregated.
  'tutorial_started',
  'tutorial_step_viewed',
  'tutorial_completed',
  'tutorial_skipped',

  // Feature reach — none of these wrote a countable doc before.
  'calendar_viewed',
  'swap_proposed',
  'swap_responded',
  'trip_created',
  'document_uploaded',
  'chore_created',
  'insights_viewed',
  'invite_shared',

  // App Store rating funnel — did the in-app prompt fire, and did anyone tap the
  // manual "Rate coparent" row? See useReviewPrompt.
  'review_prompt_shown',
  'review_prompt_manual',
]);

// Property values are bucketed, not free-form: they become map keys too.
const MAX_PROP_KEYS = 4;

function sanitizeEventKey(raw) {
  return String(raw || '')
    .toLowerCase()
    .slice(0, 48)
    .replace(/[^a-z0-9_]/g, '');
}

/**
 * Record a product event as a daily aggregate counter.
 *
 * Writes to `events/{YYYY-MM-DD}`, mirroring the shape of `pageviews/{day}`:
 * a total, a per-event breakdown, and a per-platform breakdown. Aggregate-only
 * by design — no per-user event rows, so this adds no meaningful PII surface
 * and no unbounded collection growth.
 *
 * Fire-and-forget: never awaited by UI code, never throws. Telemetry must not
 * be able to break a user flow.
 *
 * @param {string} event                  Event name; must be in TRACKED_EVENTS
 * @param {object} [props]                Low-cardinality properties (e.g. { step: 'child' })
 * @param {string} [props.platform]       'web' | 'ios' | 'android'
 */
export async function track(event, props = {}) {
  const name = sanitizeEventKey(event);
  if (!name) return;

  if (!TRACKED_EVENTS.has(name)) {
    // Loud in dev, harmless in prod — the point is to catch the typo in review.
    console.warn(`[analytics] ignoring untracked event "${event}" — add it to TRACKED_EVENTS first`);
    return;
  }

  const day = todayKey();
  const platform = sanitizeEventKey(props.platform);

  const update = {
    total: increment(1),
    [`byEvent.${name}`]: increment(1),
    updatedAt: serverTimestamp(),
  };

  if (platform) {
    update[`byPlatform.${platform}`] = increment(1);
    update[`byEventPlatform.${name}_${platform}`] = increment(1);
  }

  // Fold a bounded number of low-cardinality props into their own counters,
  // e.g. onboarding_step_completed { step: 'child' } -> byProp.onboarding_step_completed_step_child
  let propCount = 0;
  for (const [key, value] of Object.entries(props)) {
    if (key === 'platform') continue;
    if (propCount >= MAX_PROP_KEYS) break;
    const k = sanitizeEventKey(key);
    const v = sanitizeEventKey(value);
    if (!k || !v) continue;
    update[`byProp.${name}_${k}_${v}`] = increment(1);
    propCount += 1;
  }

  try {
    await setDoc(doc(getDb(), `events/${day}`), update, { merge: true });
  } catch (err) {
    console.warn('[analytics] event ping failed', err?.message || err);
  }
}

/**
 * Capture attribution from a full URL string — the mobile counterpart to the
 * pageview-driven capture on web.
 *
 * Mobile has no `document.referrer` and no query string on the location bar, so
 * the only attribution signals available without native install-referrer APIs
 * are the ones carried on a deep link:
 *
 *   coparent://invite/AB12CD
 *   https://letscoparent.us/invite/AB12CD
 *   https://letscoparent.us/?utm_source=threads&utm_campaign=launch
 *
 * An `/invite/` path buckets as 'invite' when no explicit utm_source overrides
 * it — that is the viral loop, and it is worth seeing separately from 'direct'.
 *
 * Safe to call on every deep link. Writes first-touch only once; last-touch
 * updates only when the bucketed source actually changes.
 *
 * @param {object} opts
 * @param {string} opts.url                URL that opened the app
 * @param {{get, set}} [opts.storage]      Persistent storage adapter (AsyncStorage on mobile)
 * @returns {Promise<string|null>}         The bucketed source, or null if the URL carried no signal
 */
export async function captureAttributionFromUrl({ url, storage }) {
  if (!url || typeof url !== 'string') return null;
  const store = storage || noopStorage();

  let search = '';
  let pathname = '';
  try {
    // Normalize every shape to `https://<dummy-host>/<everything else>` before
    // parsing, so the authority slot is always the throwaway host and the real
    // content always lands in the pathname.
    //
    // Swapping only the scheme does NOT work: `coparent://invite/AB12CD` would
    // become `https://invite/AB12CD`, where "invite" parses as the HOST and
    // disappears from the path — silently dropping the one signal we care most
    // about. Pushing the whole remainder into the path treats the custom
    // scheme, the https universal link, and the exp:// dev URL identically.
    const afterScheme = url.includes('://') ? url.split('://')[1] : url;
    const parsed = new URL(`https://x/${afterScheme.replace(/^\/+/, '')}`);
    search = parsed.search || '';
    pathname = parsed.pathname || '';
  } catch {
    // Fall back to a manual split so a malformed URL still yields its query.
    const qIndex = url.indexOf('?');
    if (qIndex !== -1) search = url.slice(qIndex);
    pathname = qIndex === -1 ? url : url.slice(0, qIndex);
  }

  const utm = parseUtm(search);
  const isInvite = /(^|\/)invite\//i.test(pathname);

  // An explicit utm_source always wins — a campaign link that happens to point
  // at an invite route should still attribute to the campaign.
  const source = sanitizeSource(utm.utm_source) || (isInvite ? 'invite' : null);
  if (!source) return null;

  const payload = {
    utm_source: utm.utm_source || null,
    utm_medium: utm.utm_medium || null,
    utm_campaign: utm.utm_campaign || null,
    utm_content: utm.utm_content || null,
    utm_term: utm.utm_term || null,
    referrer: null,
    source,
    ts: Date.now(),
  };

  try {
    const existingFirst = await store.get(ATTR_FIRST_TOUCH_KEY);
    if (!existingFirst) await store.set(ATTR_FIRST_TOUCH_KEY, JSON.stringify(payload));
    if (attributionMemo.lastTouchSource !== source) {
      await store.set(ATTR_LAST_TOUCH_KEY, JSON.stringify(payload));
      attributionMemo.lastTouchSource = source;
    }
  } catch {
    // Non-fatal — telemetry must never break a deep link.
  }

  return source;
}

/**
 * Read the persisted attribution payload (first-touch by default, or last-touch).
 * Returns null if storage is empty or unparseable.
 */
export async function readStoredAttribution({ storage, key = ATTR_FIRST_TOUCH_KEY } = {}) {
  const store = storage || noopStorage();
  try {
    const raw = await store.get(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// Firestore field paths disallow "/", "~", "*", "[", "]", "." — escape them so
// a path like "/blog/my-post" becomes a usable map key.
function sanitizePathKey(path) {
  return path.replace(/\//g, '_').replace(/[~*[\].]/g, '-');
}
